import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import paymentAPI from '../services/paymentAPI';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * PaymentSuccess — shown after a successful Stripe Checkout redirect.
 * Polls the backend until credits/subscription are confirmed, then
 * redirects back to the dashboard with ?payment=success so Dashboard
 * knows to refresh credits and restore any saved work.
 */
export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [phase, setPhase] = useState('polling'); // 'polling' | 'confirmed' | 'timeout'

  useEffect(() => {
    // Wait for AuthProvider to finish restoring the session.
    // Polling before auth resolves sends unauthenticated requests during
    // cold Cloud Run starts, causing unnecessary 401s and queue pile-up.
    if (authLoading) return;

    // If auth resolved but user is not authenticated (no valid cookie),
    // there's nothing meaningful to show — send to login.
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    let cancelled = false;

    // Warm up the backend immediately — wakes Cloud Run from cold start.
    // Fire-and-forget; we don't need the result, just the TCP connection.
    api.get('/health').catch(() => {});

    const poll = async () => {
      let attempts = 0;
      const maxAttempts = 30;
      let delayMs = 1000; // start at 1 s, ramp up

      while (attempts < maxAttempts && !cancelled) {
        try {
          const res = await paymentAPI.getStatus();
          const status = res.data;
          // Credits confirmed if they now have > 0 credits or an active subscription
          if (status.is_subscribed || status.credits > 0) {
            if (!cancelled) {
              setPhase('confirmed');
              setTimeout(() => navigate('/dashboard?payment=success'), 2000);
            }
            return;
          }
        } catch (_) {
          // Network errors (cold start) are expected — keep polling
        }

        attempts++;
        if (!cancelled) {
          const wait = delayMs;
          await new Promise((r) => { setTimeout(r, wait); });
          // Exponential backoff: 1s → 1.5s → 2s → 2.5s → 3s (cap)
          delayMs = Math.min(3000, delayMs + 500);
        }
      }

      // Timed out — webhook may be delayed; redirect to dashboard anyway
      // Dashboard has its own re-poll safety net
      if (!cancelled) {
        setPhase('timeout');
      }
    };

    // Brief initial delay to give the webhook time to land
    const initialDelay = setTimeout(poll, 2000);
    return () => {
      cancelled = true;
      clearTimeout(initialDelay);
    };
  }, [navigate, authLoading, isAuthenticated]);

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Icon changes based on phase */}
        <div style={styles.iconWrap} aria-hidden="true">
          {phase === 'confirmed' ? (
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
              <circle cx="28" cy="28" r="28" fill="rgba(22,163,74,0.12)" />
              <path d="M18 28l7 7 13-13" stroke="#16a34a" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : phase === 'timeout' ? (
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
              <circle cx="28" cy="28" r="28" fill="rgba(251,191,36,0.12)" />
              <path d="M28 20v10M28 34v2" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          ) : (
            <div style={styles.spinner} aria-label="Loading" />
          )}
        </div>

        <h1 style={styles.headline}>
          {phase === 'confirmed' ? 'Credits activated! 🎉' : 'Payment successful!'}
        </h1>

        <p style={styles.body}>
          {phase === 'confirmed'
            ? 'Your credits are ready. Taking you back to your work now…'
            : phase === 'timeout'
            ? 'Your payment went through but activation is taking a moment longer than expected. Please wait and then refresh the dashboard, your credits will appear shortly.'
            : 'Your purchase is confirmed. Activating your credits, this takes just a few seconds…'}
        </p>

        {sessionId && (
          <p style={styles.sessionId}>
            Session: <code style={styles.code}>{sessionId.slice(0, 24)}…</code>
          </p>
        )}

        {/* Only show the manual button if polling timed out */}
        {phase === 'timeout' && (
          <button
            id="payment-success-dashboard-btn"
            onClick={() => navigate('/dashboard?payment=success')}
            style={styles.btn}
          >
            Go to Dashboard →
          </button>
        )}

        {phase === 'polling' && (
          <p style={styles.hint}>Please don't close this tab.</p>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(160deg, #f8fafc 0%, #f0fdf4 100%)',
    fontFamily: 'var(--font-body)',
    padding: '24px',
  },
  card: {
    background: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: '1px solid rgba(22,163,74,0.2)',
    borderRadius: 'var(--radius-xl)',
    padding: '48px 40px',
    maxWidth: 460,
    width: '100%',
    textAlign: 'center',
    boxShadow: 'var(--shadow-xl)',
    animation: 'rz-fadeIn 0.5s cubic-bezier(0.16,1,0.3,1)',
  },
  iconWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 24,
  },
  spinner: {
    width: 48,
    height: 48,
    border: '3.5px solid rgba(22,163,74,0.15)',
    borderTopColor: '#16a34a',
    borderRadius: '50%',
    animation: 'rz-spin 0.9s linear infinite',
  },
  headline: {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.9rem',
    fontWeight: 800,
    letterSpacing: '-0.04em',
    color: 'var(--color-text-primary)',
    marginBottom: 12,
  },
  body: {
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6,
    marginBottom: 16,
  },
  sessionId: {
    fontSize: '0.78rem',
    color: 'var(--color-text-muted)',
    marginBottom: 16,
  },
  code: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
  },
  hint: {
    fontSize: '0.8rem',
    color: 'var(--color-text-muted)',
    marginTop: 8,
  },
  btn: {
    background: 'linear-gradient(135deg, var(--color-success), #15803d)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    padding: '12px 28px',
    fontFamily: 'var(--font-heading)',
    fontWeight: 700,
    fontSize: '0.95rem',
    cursor: 'pointer',
    transition: 'all var(--transition-base)',
    width: '100%',
    marginTop: 8,
  },
};
